import {
  Home,
  Building2,
  Briefcase,
  IndianRupee,
  Landmark,
  BadgePercent,
  RefreshCcw,
  ClipboardList,
  User,
  CalendarDays,
  FileText,
  ShieldCheck,
  Clock3,
  Users,
  Hammer,
} from "./icons";

import { EMPLOYMENT_TYPES } from "../../constants/employmentTypes";

import {
  process,
  whyMoneyMartItems,
  ctaFeatures,
  leadFormHighlights,
} from "./shared";

import {
  websiteSchema,
  organizationSchema,
  financialServiceSchema,
  localBusinessSchema,
  breadcrumbSchema,
} from "@/components/common/schema";

const schemas = [
  websiteSchema(),
  organizationSchema(),
  financialServiceSchema(),
  localBusinessSchema(),
  breadcrumbSchema([
    {
      name: "Home",
      path: "/",
    },
    {
      name: "Loan Against Property",
      path: "/loan-against-property",
    },
  ]),
];

/* -------------------------------------------------------------------------- */
/*                                    SEO                                     */
/* -------------------------------------------------------------------------- */

export const seo = {
  title: "Loan Against Property | Money Mart",

  description:
    "Unlock the value of your residential or commercial property with a Loan Against Property from leading Banks & NBFCs. Compare interest rates, higher loan amounts, and flexible repayment options with Money Mart.",

  keywords:
    "Loan Against Property, LAP, Mortgage Loan, Property Loan, Secured Loan, Money Mart",

  canonical: "/loan-against-property",

  structuredData: { schemas },
};

/* -------------------------------------------------------------------------- */
/*                                    Hero                                    */
/* -------------------------------------------------------------------------- */

export const hero = {
  badge: "Trusted Mortgage Loan Advisors",

  title: "Unlock the Value of Your Property with a Loan Against Property",

  description:
    "Need funds for business expansion, education, medical expenses, debt consolidation, or any personal requirement? Money Mart helps you compare Loan Against Property offers from leading Banks and NBFCs with expert guidance from application to disbursement.",

  highlights: [
    "High Loan Amount Eligibility",
    "Competitive Interest Rates",
    "Flexible Repayment Options",
    "Residential & Commercial Properties Accepted",
  ],

  image: "/images/loans/lap.webp",

  primaryButtonText: "Get Free Consultation",

  secondaryButtonText: "Check Eligibility",
};

/* -------------------------------------------------------------------------- */
/*                                  Overview                                  */
/* -------------------------------------------------------------------------- */

export const overview = {
  badge: "Loan Against Property",

  title: "Access Funds Without Selling Your Property",

  description:
    "A Loan Against Property (LAP) allows you to borrow against the market value of your residential or commercial property while retaining ownership.",

  items: [
    {
      icon: Home,
      title: "Residential Property",
      description:
        "Avail financing against your residential house, apartment, or plot as per lender policies.",
    },
    {
      icon: Building2,
      title: "Commercial Property",
      description:
        "Use your office, shop, warehouse, or commercial property as collateral.",
    },
    {
      icon: IndianRupee,
      title: "Higher Loan Amounts",
      description:
        "Access substantial funding based on your property's market value and eligibility.",
    },
    {
      icon: Briefcase,
      title: "Multiple End Uses",
      description:
        "Use the loan for business expansion, education, medical expenses, weddings, or other financial needs.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                  Benefits                                  */
/* -------------------------------------------------------------------------- */

export const benefits = {
  badge: "Benefits",

  title: "Why Choose a Loan Against Property?",

  description:
    "A Loan Against Property offers lower interest rates than unsecured loans while providing access to larger loan amounts.",

  items: [
    {
      icon: BadgePercent,
      title: "Lower Interest Rates",
      description:
        "Benefit from attractive interest rates compared to unsecured personal or business loans.",
    },
    {
      icon: IndianRupee,
      title: "Higher Loan Amount",
      description:
        "Borrow a significant amount based on the value of your property.",
    },
    {
      icon: RefreshCcw,
      title: "Flexible Repayment",
      description:
        "Choose repayment tenures that suit your financial situation.",
    },
    {
      icon: Clock3,
      title: "Quick Processing",
      description:
        "Our experts help streamline documentation and lender coordination.",
    },
    {
      icon: FileText,
      title: "Multiple End Uses",
      description:
        "Use the funds for business, education, medical emergencies, weddings, or debt consolidation.",
    },
    {
      icon: Users,
      title: "Expert Loan Assistance",
      description:
        "Receive end-to-end guidance from experienced mortgage loan advisors.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export const types = {
  badge: "Loan Options",

  title: "Loan Against Property Solutions",

  description:
    "Choose the right loan based on your property type and financial requirements.",

  items: [
    {
      icon: Home,
      title: "Residential Property Loan",
      description:
        "Loans against self-occupied or rented residential properties.",
    },
    {
      icon: Building2,
      title: "Commercial Property Loan",
      description:
        "Finance against offices, shops, warehouses, and commercial buildings.",
    },
    {
      icon: Briefcase,
      title: "Business Purpose Loan",
      description:
        "Raise capital for business expansion, working capital, or investments.",
    },
    {
      icon: Landmark,
      title: "Personal Requirement Loan",
      description:
        "Meet education, medical, wedding, or other personal financial needs.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                Eligibility                                 */
/* -------------------------------------------------------------------------- */

export const eligibility = {
  badge: "Eligibility",

  title: "Check Your Loan Against Property Eligibility",

  description:
    "Eligibility depends on your income, property value, repayment capacity, and lender guidelines.",

  requirements: [
    {
      icon: User,
      title: "Age",
      value: "21 – 70 Years",
    },
    {
      icon: Briefcase,
      title: "Employment",
      value: "Salaried or Self-Employed",
    },
    {
      icon: IndianRupee,
      title: "Income",
      value: "Stable & Verifiable Source of Income",
    },
    {
      icon: CalendarDays,
      title: "Property",
      value: "Residential or Commercial Property",
    },
  ],

  approvalFactors: [
    "Property market value",
    "Income & repayment capacity",
    "Credit score & repayment history",
    "Existing financial liabilities",
    "Property ownership & legal verification",
    "Employment or business stability",
  ],

  cta: {
    title: "Not sure if you're eligible?",

    description:
      "Our mortgage loan experts will assess your profile and recommend the best lender for your requirements.",

    buttonText: "Check Eligibility Now",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Documents                                  */
/* -------------------------------------------------------------------------- */

export const documents = {
  badge: "Documents Required",

  title: "Documents Required for a Loan Against Property",

  description:
    "The exact document requirements vary by lender and applicant profile. These are the commonly required documents for most Loan Against Property applications.",

  groups: [
    {
      icon: Users,
      title: "Documents Required for All Applicants",

      documents: [
        "1 Selfie/Photograph (Applicant & Co-applicant)",
        "PAN Card (Applicant & Co-applicant)",
        "Latest Downloaded Aadhaar Card (Applicant & Co-applicant)",
      ],
    },
    {
      icon: Briefcase,
      title: "Additional Documents for Salaried Applicants",

      documents: [
        "Last 3 Months Salary Slips",
        "Last 2 Years Form 16",
        "Salary & Savings Account Statements for the Last 7 Months",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: Building2,
      title: "Additional Documents for Self-Employed Applicants",

      documents: [
        "GST Registration",
        "UDYAM Registration",
        "Last 3 Years ITR with Profit & Loss Statement & Balance Sheet",
        "Business Bank Statements",
        "Existing Loan Details (if applicable)",
      ],
    },
    {
      icon: Home,
      title: "Property Documents",

      documents: [
        "Complete Chain of Property Documents",
        "Latest Property Tax Receipt",
        "Approved Building Plan (if applicable)",
        "Property Ownership Documents",
      ],
    },
    {
      icon: Hammer,
      title: "Additional Supporting Documents",

      documents: [
        "Property Valuation Documents (if requested)",
        "NOC or Society Documents (where applicable)",
        "Any Additional Documents Requested by the Lender",
      ],
    },
    {
      icon: RefreshCcw,
      title: "For Balance Transfer Cases",

      documents: [
        "Existing Loan Sanction Letter",
        "Loan Statement",
        "Outstanding / Foreclosure Letter",
        "List of Original Property Documents",
      ],
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                   Process                                  */
/* -------------------------------------------------------------------------- */

export const loanProcess = {
  ...process,

  title: "Your Loan Against Property in 6 Simple Steps",

  description:
    "From property evaluation to loan disbursement, our mortgage specialists guide you through every stage of the process.",
};

/* -------------------------------------------------------------------------- */
/*                              Why Money Mart                                */
/* -------------------------------------------------------------------------- */

export const whyMoneyMart = {
  badge: "Why Money Mart",

  title: "Why Apply Through Money Mart Instead of Visiting Multiple Banks?",

  description:
    "Compare Loan Against Property offers from multiple lenders through a single application while receiving personalized guidance from experienced mortgage loan advisors.",

  items: whyMoneyMartItems,
};

/* -------------------------------------------------------------------------- */
/*                                     FAQ                                    */
/* -------------------------------------------------------------------------- */

export const faq = {
  badge: "Frequently Asked Questions",

  title: "Loan Against Property FAQs",

  description:
    "Find answers to the most commonly asked questions about Loan Against Property.",

  items: [
    {
      question: "What is a Loan Against Property?",

      answer:
        "It is a secured loan where you pledge your residential or commercial property as collateral while retaining ownership.",
    },
    {
      question: "Can I continue using my property?",

      answer:
        "Yes. You continue to own and use the property as usual while the loan remains active.",
    },
    {
      question: "How much loan can I get?",

      answer:
        "The loan amount depends on the property's market value, your income, repayment capacity, and lender policies.",
    },
    {
      question: "Can I use the funds for any purpose?",

      answer:
        "Yes. Subject to lender policies, the funds may be used for business expansion, education, medical expenses, debt consolidation, weddings, and other legitimate financial requirements.",
    },
    {
      question: "How long does approval take?",

      answer:
        "Approval timelines vary by lender but generally take a few working days after successful document verification and property evaluation.",
    },
    {
      question: "What properties are accepted?",

      answer:
        "Most lenders accept eligible residential and commercial properties, subject to legal and technical verification.",
    },
  ],
};

/* -------------------------------------------------------------------------- */
/*                                     CTA                                    */
/* -------------------------------------------------------------------------- */

export const cta = {
  badge: "Need Funds Without Selling Your Property?",

  title: "Let's Find the Right Loan Against Property for You",

  description:
    "Compare mortgage loan offers from leading Banks and NBFCs with expert assistance throughout your borrowing journey.",

  features: ctaFeatures,

  primaryButton: {
    text: "Apply for Loan Against Property",
  },

  secondaryButton: {
    text: "Call +91 92144 17434",
    href: "tel:+919214417434",
  },
};

/* -------------------------------------------------------------------------- */
/*                                 Lead Form                                  */
/* -------------------------------------------------------------------------- */

export const leadForm = {
  badge: "Free Mortgage Loan Consultation",

  title: "Speak to a Loan Against Property Expert",

  description:
    "Our mortgage loan advisors will compare multiple lenders and help you secure the best Loan Against Property based on your requirements.",

  highlights: leadFormHighlights,

  phone: {
    label: "Call us directly",
    text: "+91 92144 17434",
    href: "tel:+919214417434",
  },

  form: {
    title: "Get Free Consultation",

    description:
      "Fill in your details and our mortgage loan expert will contact you shortly.",

    placeholders: {
      name: "Full Name *",
      mobile: "Mobile Number *",
      employment: "Employment Type",
      income: "Monthly Income",
      loanAmount: "Required Loan Amount",
    },

    incomeField: "monthlyIncome",

    employmentOptions: EMPLOYMENT_TYPES,

    incomeOptions: [
      {
        value: "below-25k",
        label: "Below ₹25,000",
      },
      {
        value: "25k-50k",
        label: "₹25,000 – ₹50,000",
      },
      {
        value: "50k-1L",
        label: "₹50,000 – ₹1,00,000",
      },
      {
        value: "1L-2L",
        label: "₹1,00,000 – ₹2,00,000",
      },
      {
        value: "2L-plus",
        label: "Above ₹2,00,000",
      },
    ],

    submitButton: "Get Free Consultation",

    privacyText:
      "Your information is secure and will only be used to assist with your loan enquiry. No spam. No hidden charges.",
  },
};

/* -------------------------------------------------------------------------- */
/*                                  Export                                    */
/* -------------------------------------------------------------------------- */

const loanAgainstProperty = {
  seo,
  hero,
  overview,
  benefits,
  types,
  eligibility,
  documents,
  process: loanProcess,
  whyMoneyMart,
  faq,
  cta,
  leadForm,
};

export default loanAgainstProperty;
